import React, { useState } from "react";
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
} from "firebase/auth";
import {
  doc,
  getDoc,
  setDoc,
  serverTimestamp,
} from "firebase/firestore";
import { auth, db } from "../firebase";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ShieldCheck, Moon, Sun, Loader2 } from "lucide-react";

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [newRole, setNewRole] = useState("agent");
  const [isRegister, setIsRegister] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [theme, setTheme] = useState("dark");

  const navigate = useNavigate();

  const PAYSTACK_KEY =
    import.meta.env?.VITE_PAYSTACK_KEY ||
    process.env.REACT_APP_PAYSTACK_KEY;

  const isPaystackReady =
    typeof window !== "undefined" &&
    typeof window.PaystackPop !== "undefined";

  /* ================= SUBMIT ================= */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    const cleanEmail = email.trim();
    const cleanPassword = password.trim();

    try {
      /* ================= REGISTER ================= */
      if (isRegister) {
        if (!cleanEmail || !cleanPassword) {
          setError("Email and password are required.");
          return;
        }

        if (cleanPassword.length < 6) {
          setError("Password must be at least 6 characters.");
          return;
        }

        /* ===== ADMIN REGISTRATION (APPROVAL REQUIRED) ===== */
        if (newRole === "admin") {
          const cred = await createUserWithEmailAndPassword(
            auth,
            cleanEmail,
            cleanPassword
          );

          await setDoc(doc(db, "users", cred.user.uid), {
            email: cleanEmail,
            role: "admin",
            approved: false,
            paid: false,
            createdAt: serverTimestamp(),
          });

          alert("Admin account created. Awaiting approval.");
          await auth.signOut();
          setIsRegister(false);
          setEmail("");
          setPassword("");
          return;
        }

        /* ===== AGENT REGISTRATION (PAY FIRST) ===== */
        if (newRole === "agent") {
          if (!isPaystackReady) {
            setError("Payment system not ready. Refresh page.");
            return;
          }

          if (!PAYSTACK_KEY) {
            setError("Payment key missing.");
            return;
          }

          const handler = window.PaystackPop.setup({
            key: PAYSTACK_KEY,
            email: cleanEmail,
            amount: 50 * 100, // GHS 50
            currency: "GHS",

            callback: function (response) {
              (async () => {
                try {
                  const cred = await createUserWithEmailAndPassword(
                    auth,
                    cleanEmail,
                    cleanPassword
                  );

                  await setDoc(doc(db, "users", cred.user.uid), {
                    email: cleanEmail,
                    role: "agent",
                    approved: true,
                    paid: true,
                    reference: response.reference,
                    createdAt: serverTimestamp(),
                  });

                  alert("Agent account created successfully!");
                  setIsRegister(false);
                  setEmail("");
                  setPassword("");
                } catch (err) {
                  console.error(err);
                  setError("Account creation failed after payment.");
                } finally {
                  setLoading(false);
                }
              })();
            },

            onClose: function () {
              setError("Payment cancelled.");
              setLoading(false);
            },
          });

          handler.openIframe();
          return;
        }
      }

      /* ================= LOGIN ================= */
      const cred = await signInWithEmailAndPassword(
        auth,
        cleanEmail,
        cleanPassword
      );

      const snap = await getDoc(doc(db, "users", cred.user.uid));

      if (!snap.exists()) {
        setError("Account not configured. Contact administrator.");
        await auth.signOut();
        return;
      }

      const data = snap.data();

      if (data.role === "admin" && !data.approved) {
        setError("Admin account pending approval.");
        await auth.signOut();
        return;
      }

      if (data.role === "admin") navigate("/admin/dashboard");
      else if (data.role === "agent") navigate("/agent/dashboard");
      else navigate("/");

    } catch (err) {
      console.error(err);
      setError(err.message || "Authentication failed.");
    } finally {
      setLoading(false);
    }
  };

  const toggleTheme = () =>
    setTheme(theme === "dark" ? "light" : "dark");

  /* ================= STYLES ================= */
  const containerClass =
    theme === "dark"
      ? "min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-black to-sky-900 p-4"
      : "min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-100 via-gray-200 to-gray-300 p-4";

  const cardClass =
    theme === "dark"
      ? "w-full max-w-md bg-white/10 backdrop-blur-xl p-8 rounded-3xl shadow-2xl border border-white/10 text-white"
      : "w-full max-w-md bg-white/90 backdrop-blur-xl p-8 rounded-3xl shadow-2xl border border-gray-300 text-black";

  const inputClass =
    "w-full p-3 rounded-xl outline-none transition focus:ring-2 " +
    (theme === "dark"
      ? "bg-black/40 text-white placeholder-gray-400 focus:ring-sky-500"
      : "bg-gray-100 text-black placeholder-gray-500 focus:ring-blue-500");

  const buttonClass =
    "w-full p-3 rounded-xl font-bold flex items-center justify-center gap-2 transition " +
    (loading
      ? "opacity-60 cursor-not-allowed"
      : theme === "dark"
      ? "bg-sky-600 hover:bg-sky-500"
      : "bg-blue-600 hover:bg-blue-500");

  const errorClass =
    theme === "dark"
      ? "bg-red-500/10 border border-red-500/20 text-red-400 p-3 rounded-xl text-sm text-center"
      : "bg-red-100 border border-red-300 text-red-700 p-3 rounded-xl text-sm text-center";

  /* ================= UI ================= */
  return (
    <div className={containerClass}>
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className={cardClass}
      >
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-3xl font-extrabold text-sky-400 flex items-center gap-2">
            <ShieldCheck /> Admin Access
          </h2>

          <button onClick={toggleTheme} className="p-2 rounded-full bg-white/10">
            {theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}
          </button>
        </div>

        {error && <div className={errorClass}>{error}</div>}

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <input
            type="email"
            placeholder="Email address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={inputClass}
            required
          />

          <input
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className={inputClass}
            required
          />

          {isRegister && (
            <select
              value={newRole}
              onChange={(e) => setNewRole(e.target.value)}
              className={inputClass}
            >
              <option value="agent">Agent</option>
              <option value="admin">Admin</option>
            </select>
          )}

          <motion.button
            whileTap={{ scale: 0.97 }}
            type="submit"
            disabled={loading}
            className={buttonClass}
          >
            {loading ? (
              <>
                <Loader2 className="animate-spin" size={18} />
                Please wait…
              </>
            ) : isRegister ? (
              "Create Account"
            ) : (
              "Sign In"
            )}
          </motion.button>
        </form>

        <button
          onClick={() => setIsRegister(!isRegister)}
          className="text-center text-sm mt-4 font-semibold text-sky-400 hover:text-sky-300"
        >
          {isRegister
            ? "Already have an account? Sign In"
            : "Create new user account"}
        </button>
      </motion.div>
    </div>
  );
}
